import cx from 'classnames';
import PropTypes from 'prop-types';
import * as React from 'react';
import shallowCompare from 'react-addons-shallow-compare';
import { throttle } from 'lodash';

import DOM from 'lib/DOM';

import './styles/ScrollableArea.scss';

type Props = {
  scrollToBottom?: boolean | null;
  showShadows?: boolean | null;
  onScroll?: () => void | null;
  useBodyChildrenHeight?: boolean | null;
  children?: React.ReactNode;
  alignBottom: boolean | null;
  className?: string | null;
  style?: React.CSSProperties;
  onScrollToBottom?: Function;
  maxHeight?: number;
};

type State = {
  scrollHeight: number;
  scrollBottom: number;
  scrollTop: number;
  scrolledToBottom: boolean;
  scrolledToTop: boolean;
};

class ScrollableArea extends React.Component<Props, State> {
  static childContextTypes = {
    scrollableArea: PropTypes.object,
  };

  static defaultProps = {
    scrollToBottom: false,
    showShadows: false,
    useBodyChildrenHeight: false,
    alignBottom: false,
  };

  constructor(props: Props) {
    super(props);

    this.body = React.createRef();
    this.state = {
      scrollHeight: 0,
      scrollBottom: 0,
      scrollTop: 0,
      scrolledToBottom: !!props.scrollToBottom,
      scrolledToTop: !props.scrollToBottom,
    };
  }

  body: React.RefObject<HTMLDivElement>;

  getChildContext(): { scrollableArea: ScrollableArea } {
    return {
      scrollableArea: this,
    };
  }

  componentDidMount() {
    this._mounted = true;
    if (this.props.scrollToBottom) {
      this.scrollToBottom();

      if (this.props.onScrollToBottom) {
        this.props.onScrollToBottom();
      }
    }

    // HACK We need this to render correct child scrollHeight in getScrollHeight
    setTimeout(() => {
      this._mounted && this.updateHeight();
    }, 0);
  }

  shouldComponentUpdate(nextProps: Props, nextState: State): boolean {
    return shallowCompare(this, nextProps, nextState);
  }

  componentWillUpdate() {
    const { current } = this.body;
    if (!current) {
      return;
    }

    this.prevScrolledHeight = current.scrollHeight;
    this.prevScrollTop = current.scrollTop;
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const scrolledToBottom = !prevState.scrolledToBottom && this.state.scrolledToBottom;

    if (scrolledToBottom && this.props.onScrollToBottom) {
      this.props.onScrollToBottom();
    }

    if (this.props.scrollToBottom) {
      const { current } = this.body;
      if (this.props.children !== prevProps.children && current) {
        current.scrollTop = this.prevScrollTop + (current.scrollHeight - this.prevScrolledHeight);
      }
    }

    if (
      this.props.children !== prevProps.children ||
      (this.props.style && prevProps.style && this.props.style.height !== prevProps.style.height)
    ) {
      this.updateHeight();
    }
  }

  componentWillUnmount() {
    this._mounted = false;
  }

  _mounted = false;

  prevScrollTop = 0;
  prevScrolledHeight = 0;

  // TODO: Look into why body is using absolute positioning to replace this workaround
  getScrollHeight = () => {
    const { current } = this.body;

    // component may be unmounting
    if (!current) {
      return this.prevScrolledHeight;
    }

    let scrollHeight;
    if (this.props.useBodyChildrenHeight) {
      const children = Array.prototype.slice.call(current.children);
      scrollHeight = children
        .map((child) => child.scrollHeight)
        .reduce((prev, curr) => prev + curr, 0);
    } else {
      scrollHeight = current.scrollHeight;
    }

    // Account for the parents border (assumes equal border width on all sides)
    const parentBorderWidth = DOM.getStyle(current.parentNode as HTMLElement, 'border-width');
    if (parentBorderWidth) {
      scrollHeight += 2 * parseInt(parentBorderWidth, 10);
    }

    return scrollHeight;
  };

  handleScroll = throttle(() => {
    if (this.body.current && this.props.onScroll) {
      this.updateScrollPositions(this.props.onScroll);
    }
  }, 100);

  handleWheel = (event: React.WheelEvent) => {
    if (!this._mounted) {
      return;
    }

    if (event.deltaX && !event.deltaY) {
      return;
    }

    // This prevents mousewheel events on ScrollableArea instances from transferring to mousewheel
    // on the document's main scroll area.
    const delta = -event.deltaY;
    const node = this.body.current;
    if (!node) {
      return;
    }
    const scrollHeight = node.scrollHeight;
    const clientHeight = node.clientHeight;
    if (scrollHeight > clientHeight) {
      const scrollTop = node.scrollTop;
      if (
        (delta > 0 && scrollTop === 0) ||
        (delta < 0 && scrollTop >= scrollHeight - clientHeight - 1)
      ) {
        event.preventDefault();
      } else {
        event.stopPropagation();
      }
    }
  };

  isScrolledToBottom = (offset: number = 0) => {
    return this.state.scrollBottom + offset >= 0;
  };

  isScrolledToTop = (offset: number = 0) => {
    return this.state.scrollTop - offset <= 0;
  };

  scrollIntoView = (node: Element) => {
    const { current } = this.body;
    if (!current) {
      return;
    }

    const position = DOM.getPosition(node as HTMLElement, current);
    if (position.top < 0) {
      current.scrollTop += position.top;
    } else if (position.top + (node as HTMLElement).offsetHeight > current.offsetHeight) {
      current.scrollTop += position.top + (node as HTMLElement).offsetHeight - current.offsetHeight;
    }
  };

  scrollIntoViewCenter = (node: Element) => {
    const { current } = this.body;
    if (!current) {
      return;
    }

    const position = DOM.getPosition(node as HTMLElement, current);
    current.scrollTop +=
      position.top - (current.offsetHeight - (node as HTMLElement).offsetHeight) / 2;
  };

  scrollToTopOfElement = (node: Element) => {
    const { current } = this.body;
    if (!current) {
      return;
    }

    const position = DOM.getPosition(node as HTMLElement, current);
    current.scrollTop += position.top;
  };

  scrollToBottom = () => {
    const { current } = this.body;
    if (!current) {
      return;
    }

    current.scrollTop = current.scrollHeight;
  };

  scrollToTop = () => {
    const { current } = this.body;
    if (!current) {
      return;
    }

    current.scrollTop = 0;
  };

  updateHeight = () => {
    this.setState(
      {
        scrollHeight: this.getScrollHeight(),
      },
      () => {
        // If the component is re-rendered before this is called, the refs become undefined.
        if (this.body.current) {
          this.updateScrollPositions();
        }
      }
    );
  };

  updateScrollPositions = (callback?: () => void) => {
    const { current } = this.body;
    if (!current) {
      return;
    }

    const scrollTop = current.scrollTop;
    const scrollBottom = scrollTop + current.offsetHeight - current.scrollHeight;

    this.setState(
      {
        scrollTop,
        scrollBottom,
        scrolledToBottom: Math.abs(scrollBottom) <= 1,
        scrolledToTop: Math.abs(scrollTop) <= 1,
      },
      callback || undefined
    );
  };

  render() {
    const { alignBottom, className, style, showShadows, children, maxHeight } = this.props;

    return (
      <div
        className={cx(
          {
            ScrollableArea: true,
            'ScrollableArea-hasContentAbove': showShadows && !this.state.scrolledToTop,
            'ScrollableArea-hasContentBelow': showShadows && !this.state.scrolledToBottom,
          },
          className
        )}
        style={Object.assign({ height: this.state.scrollHeight, maxHeight }, style)}
      >
        <div
          ref={this.body}
          className={cx({
            'ScrollableArea-body': true,
            'ScrollableArea-body-bottom': alignBottom,
          })}
          onScroll={this.handleScroll}
          onWheel={this.handleWheel}
        >
          {children}
        </div>
      </div>
    );
  }
}

export default ScrollableArea;
